import {
  AbstractControl,
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { CheckoutUpsellExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { RuleEntity } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { PRODUCT_QUERYABLE_RULE_ENTITIES } from '@pdqdev/pdq-shared/dist/utils/extensions-rules-to-graphql';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { getExtensionRules } from '@pages/checkout-app/checkout-layout/main/components/extensions/shared/all-extension-rules';
import { DEFAULT_DISCOUNT_LABEL_COLOR, DiscountType } from '@pages/engage/smart-cart/smart-cart-form/discount.model';

/**
 * Product Selection tab form model (co-located with its component).
 *
 * `createProductSelectionFormGroup().getRawValue()` is the `productSelection` slice of the widget's
 * persisted `settings` JSON. Mirrors the checkout-upsell extension's product-selection config (same
 * mode/prioritize VALUES, reused from `@pdqdev/pdq-shared`) MINUS every discount control — discounts
 * are out of scope.
 */

// Narrowed to the values this widget actually supports (kept tied to the shared enum members):
// products-in-cart is Any/Smart only (no `manual`); upsell mode excludes the legacy `metadata`. Keeps
// the type in sync with the editor's option lists so an unsupported value can't leak into the config.
export type ProductsInCartMode =
  | `${CheckoutUpsellExtension.ProductsInCartMode.ANY}`
  | `${CheckoutUpsellExtension.ProductsInCartMode.SMART}`;
export type UpsellRecommendedProductsMode =
  | `${CheckoutUpsellExtension.UpsellRecommendedProductsMode.SHOPIFY_RECOMMENDATIONS}`
  | `${CheckoutUpsellExtension.UpsellRecommendedProductsMode.MANUAL}`
  | `${CheckoutUpsellExtension.UpsellRecommendedProductsMode.SMART}`;
export type HideProductsAlreadyInCart = CheckoutUpsellExtension.HideProductsAlreadyInCart;
export type NoItemsToDisplay = CheckoutUpsellExtension.NoItemsToDisplay;

// Ordering set matching the checkout-upsell extension enum ('manual' = keep the merchant's picked order).
export type PrioritizeRecommendedItemsBy = 'profitability' | 'high-price' | 'low-price' | 'selling-plan' | 'manual';

const DEFAULT_PRIORITIZE_BY: PrioritizeRecommendedItemsBy = 'high-price';
const PRIORITIZE_OPTIONS: PrioritizeRecommendedItemsBy[] = [
  'profitability',
  'high-price',
  'low-price',
  'selling-plan',
  'manual',
];

// Coerce a persisted value to a currently-supported option. A config saved with a since-removed option
// (e.g. the retired "profitability for free shipping") would otherwise keep a dead value — an unselected
// radio that re-persists on save — so fall back to the default instead.
const coercePrioritizeBy = (value: unknown): PrioritizeRecommendedItemsBy =>
  PRIORITIZE_OPTIONS.includes(value as PrioritizeRecommendedItemsBy)
    ? (value as PrioritizeRecommendedItemsBy)
    : DEFAULT_PRIORITIZE_BY;

// Products-in-cart trigger rules the backend actually evaluates — whitelisted so merchants can't
// configure a rule that silently never matches. Geo + post-order entities are excluded (unavailable
// pre-checkout).
export const PRODUCTS_IN_CART_RULE_ENTITIES: RuleEntity[] = [
  RuleEntity.PRODUCT_TYPE,
  RuleEntity.PRODUCT_TAG,
  RuleEntity.SKU,
  RuleEntity.BARCODE,
  RuleEntity.COLLECTION,
  RuleEntity.VENDOR,
  RuleEntity.CART_VALUE,
  RuleEntity.ITEM_COUNT,
  RuleEntity.CART_ATTRIBUTE,
  RuleEntity.CART_ATTRIBUTE_KEY,
  RuleEntity.CART_ATTRIBUTE_VALUE,
  RuleEntity.CUSTOMER_TAGS,
  RuleEntity.CUSTOMER_ORDER_COUNT,
  RuleEntity.CUSTOMER_SPEND,
  RuleEntity.PRODUCT_METAFIELD,
  RuleEntity.VARIANT_METAFIELD,
  RuleEntity.GROSS_MARGIN,
  RuleEntity.WEIGHT,
  RuleEntity.SELLING_PLAN,
  RuleEntity.PROPERTIES_KEY,
  RuleEntity.PROPERTIES_VALUE,
  RuleEntity.COUPONS,
  RuleEntity.HAS_DISCOUNT,
  RuleEntity.DEVICE_TYPE,
  RuleEntity.TIME_OF_DAY_SEGMENTATION,
  RuleEntity.WEEKDAY_SEGMENTATION,
  RuleEntity.DELIVERY_AREA_COUNTRIES,
  RuleEntity.DELIVERY_AREA_STATES,
  RuleEntity.DELIVERY_AREA_ZIP_CODES,
  RuleEntity.DELIVERY_AREA_DESTINATIONS,
];

const DEFAULT_MAX_ITEMS_TO_DISPLAY = 10;

export interface RuleGroupValue {
  id?: number;
  productsInCart?: { mode?: ProductsInCartMode; rules?: unknown[] };
  upsellRecommendedProducts?: { mode?: UpsellRecommendedProductsMode; products?: unknown[]; rules?: unknown[] };
  inStockProductsOnly?: boolean;
  matchFulfillmentCenterToCartItems?: boolean;
  includeDigitalItems?: boolean;
  matchProductVariants?: boolean;
  hideProductsAlreadyInCart?: HideProductsAlreadyInCart;
  maxItemsToDisplay?: number;
  NoItemsToDisplay?: NoItemsToDisplay;
  prioritizeRecommendedItemsBy?: PrioritizeRecommendedItemsBy;
  sellingPlanId?: string | number;
  sellingPlanDescription?: string;
  discountConfiguration?: DiscountConfigurationValue;
}

/** Same keys as the other Smart Cart widgets' discount config, so the backend has one reader. */
export interface DiscountConfigurationValue {
  enabled?: boolean;
  discountType?: DiscountType;
  value?: number;
  labelText?: string;
  labelColor?: string;
}

export interface ProductSelectionValue {
  ruleGroups?: RuleGroupValue[];
  style?: Record<string, unknown>;
  settings?: Record<string, unknown>;
}

export const DEFAULT_RULE_GROUP: Omit<RuleGroupValue, 'id'> = {
  productsInCart: { mode: CheckoutUpsellExtension.ProductsInCartMode.ANY, rules: [] },
  upsellRecommendedProducts: {
    mode: CheckoutUpsellExtension.UpsellRecommendedProductsMode.SHOPIFY_RECOMMENDATIONS,
    products: [],
    rules: [],
  },
  inStockProductsOnly: true,
  matchFulfillmentCenterToCartItems: false,
  includeDigitalItems: true,
  matchProductVariants: false,
  hideProductsAlreadyInCart: 'parentProduct',
  maxItemsToDisplay: DEFAULT_MAX_ITEMS_TO_DISPLAY,
  NoItemsToDisplay: 'showShopifyRecommendations',
  prioritizeRecommendedItemsBy: DEFAULT_PRIORITIZE_BY,
  discountConfiguration: {
    enabled: false,
    discountType: 'fixedAmount',
    value: 10,
    labelText: '',
    labelColor: DEFAULT_DISCOUNT_LABEL_COLOR,
  },
};

/**
 * One product rule group (OR-combined with its siblings). Mirrors the checkout-upsell rule group
 * exactly (there is no discount field on the group itself). Rules FormArrays are built via the
 * shared `RulesDynamicFormService` so the smart-search UI (`pdq-rules-dynamic-form`) binds directly.
 */
/**
 * Rule-group validator: when "Selling plan" prioritization is chosen, the Selling Plan ID is
 * required and the description (if provided) must contain the `{price}` placeholder. Mirrors the
 * checkout-upsell extension's `sellingPlanValidator`.
 */
export function sellingPlanValidator(group: AbstractControl): ValidationErrors | null {
  if (group.get('prioritizeRecommendedItemsBy')?.value !== 'selling-plan') {
    return null;
  }
  if (!group.get('sellingPlanId')?.value) {
    return { sellingPlanIdRequired: true };
  }
  const description = group.get('sellingPlanDescription')?.value as string | undefined;
  if (description && !description.includes('{price}')) {
    return { missingPricePlaceholder: true };
  }
  return null;
}

export function createRuleGroupFormGroup(
  id: number,
  values: RuleGroupValue,
  rulesDynamicFormService?: RulesDynamicFormService,
): UntypedFormGroup {
  const productsInCartRules = getExtensionRules();
  const upsellProductsRules = getExtensionRules({ onlyRules: PRODUCT_QUERYABLE_RULE_ENTITIES });

  const initRules = (rules: unknown[] | undefined, config: ReturnType<typeof getExtensionRules>): UntypedFormArray =>
    rulesDynamicFormService
      ? rulesDynamicFormService.initRulesFormArray(((rules ?? []) as unknown[]).flat(), config)
      : new UntypedFormArray([]);

  return new UntypedFormGroup(
    {
      id: new UntypedFormControl(values.id ?? id),
      productsInCart: new UntypedFormGroup({
        mode: new UntypedFormControl(values.productsInCart?.mode ?? CheckoutUpsellExtension.ProductsInCartMode.ANY),
        rules: initRules(values.productsInCart?.rules ?? [], productsInCartRules),
      }),
      upsellRecommendedProducts: new UntypedFormGroup({
        mode: new UntypedFormControl(
          values.upsellRecommendedProducts?.mode ??
            CheckoutUpsellExtension.UpsellRecommendedProductsMode.SHOPIFY_RECOMMENDATIONS,
        ),
        products: new UntypedFormControl(values.upsellRecommendedProducts?.products ?? []),
        rules: initRules(values.upsellRecommendedProducts?.rules ?? [], upsellProductsRules),
      }),
      inStockProductsOnly: new UntypedFormControl(values.inStockProductsOnly ?? true),
      matchFulfillmentCenterToCartItems: new UntypedFormControl(values.matchFulfillmentCenterToCartItems ?? false),
      includeDigitalItems: new UntypedFormControl(values.includeDigitalItems ?? true),
      matchProductVariants: new UntypedFormControl(values.matchProductVariants ?? false),
      hideProductsAlreadyInCart: new UntypedFormControl(values.hideProductsAlreadyInCart ?? 'parentProduct'),
      maxItemsToDisplay: new UntypedFormControl(values.maxItemsToDisplay ?? DEFAULT_MAX_ITEMS_TO_DISPLAY),
      NoItemsToDisplay: new UntypedFormControl(values.NoItemsToDisplay ?? 'showShopifyRecommendations'),
      prioritizeRecommendedItemsBy: new UntypedFormControl(coercePrioritizeBy(values.prioritizeRecommendedItemsBy)),
      sellingPlanId: new UntypedFormControl(values.sellingPlanId != null ? String(values.sellingPlanId) : ''),
      sellingPlanDescription: new UntypedFormControl(
        values.sellingPlanDescription ?? '<p>Today Only! $0! <s><b>{price}</b></s></p>',
      ),
      // Only reachable in Manual / Smart mode — Automatic (Shopify recommendations) can't be discounted.
      discountConfiguration: new FormGroup({
        enabled: new FormControl(values.discountConfiguration?.enabled ?? false, { nonNullable: true }),
        discountType: new FormControl<DiscountType>(values.discountConfiguration?.discountType ?? 'fixedAmount', {
          nonNullable: true,
        }),
        value: new FormControl(values.discountConfiguration?.value ?? 10, { nonNullable: true }),
        labelText: new FormControl(values.discountConfiguration?.labelText ?? '', { nonNullable: true }),
        labelColor: new FormControl(values.discountConfiguration?.labelColor ?? DEFAULT_DISCOUNT_LABEL_COLOR, {
          nonNullable: true,
        }),
      }),
    },
    { validators: sellingPlanValidator },
  );
}

/**
 * A Design box (Main Background / Product Item / Image): a color OR fit, + border/corner/padding.
 * border/corner/padding default to null (not 0) so "unset" is distinguishable from an explicit 0:
 * the preview + runtime render the design base for null and honor a merchant-set 0 (no border / square
 * corners / no padding). A saved 0 loads back as 0.
 */
function createBoxStylingGroup(values: Record<string, unknown> | undefined, lead: 'color' | 'fit'): UntypedFormGroup {
  return new UntypedFormGroup({
    ...(lead === 'color'
      ? { color: new UntypedFormControl(values?.['color'] ?? '#FFFFFF') }
      : { fit: new UntypedFormControl(values?.['fit'] ?? 'square') }),
    borderWidth: new UntypedFormControl(values?.['borderWidth'] ?? null),
    cornerRadius: new UntypedFormControl(values?.['cornerRadius'] ?? null),
    innerPadding: new UntypedFormControl(values?.['innerPadding'] ?? null),
  });
}

// Additional display texts (Display vendor / Display product type …) — a reorderable list mirroring
// the checkout-upsell extension: each item has a type, an on/off `display`, a `{tag}` `text`
// template, and a `priority` (list order).
export interface AdditionalDisplayTextValue {
  type: string;
  display?: boolean;
  text?: string;
  priority?: number;
}

const DEFAULT_ADDITIONAL_DISPLAY_TEXTS: AdditionalDisplayTextValue[] = [
  { type: 'vendor', display: false, text: 'By: {vendor}', priority: 0 },
  { type: 'product-type', display: false, text: 'Type: {product-type}', priority: 1 },
];

function createAdditionalDisplayTextGroup(value: AdditionalDisplayTextValue): UntypedFormGroup {
  return new UntypedFormGroup(
    {
      type: new UntypedFormControl(value.type),
      display: new UntypedFormControl(value.display ?? false),
      text: new UntypedFormControl(value.text ?? `{${value.type}}`),
      priority: new UntypedFormControl(value.priority ?? 0),
    },
    // Group-level so it re-runs whenever the display toggle flips. The {tag} is only required while
    // the row is enabled — a hidden row must never block the whole Smart Cart save.
    (group: AbstractControl) => {
      if (group.get('display')?.value !== true) return null;
      const tag = `{${group.get('type')?.value}}`;
      return (group.get('text')?.value as string | undefined)?.includes(tag)
        ? null
        : { additionalPattern: `${tag} must be included` };
    },
  );
}

function createAdditionalDisplayTexts(values?: AdditionalDisplayTextValue[]): UntypedFormArray {
  const items = values?.length ? values : DEFAULT_ADDITIONAL_DISPLAY_TEXTS;
  return new UntypedFormArray(items.map(createAdditionalDisplayTextGroup));
}

/** Appearance controls (discount badge intentionally omitted — discounts are out of scope). */
function createAppearanceStyleGroup(values?: Record<string, unknown>): UntypedFormGroup {
  return new UntypedFormGroup({
    displayType: new UntypedFormControl(values?.['displayType'] ?? CheckoutUpsellExtension.DisplayType.CARDS),
    carouselNavigationStyle: new UntypedFormControl(
      values?.['carouselNavigationStyle'] ?? CheckoutUpsellExtension.CarouselNavigationStyle.ARROWS,
    ),
    // Rows shown when displayType = 'row' (List) — extension parity (default 1).
    numberOfRowsInRowDisplayType: new UntypedFormControl(values?.['numberOfRowsInRowDisplayType'] ?? 1),
    mainBackground: createBoxStylingGroup(values?.['mainBackground'] as Record<string, unknown> | undefined, 'color'),
    productItem: createBoxStylingGroup(values?.['productItem'] as Record<string, unknown> | undefined, 'color'),
    image: createBoxStylingGroup(values?.['image'] as Record<string, unknown> | undefined, 'fit'),
  });
}

function createAppearanceSettingsGroup(values?: Record<string, unknown>): UntypedFormGroup {
  return new UntypedFormGroup({
    sectionTitle: new UntypedFormControl(values?.['sectionTitle'] ?? '<p>You may also like</p>'),
    // Empty means inherit the storefront theme's colour, which is what the title did before this
    // control existed — so saved widgets keep their current look.
    sectionTitleColor: new UntypedFormControl(values?.['sectionTitleColor'] ?? ''),
    variantSelector: new UntypedFormControl(values?.['variantSelector'] ?? 'inside'),
    // Strike-through the original price when a variant is on sale (compare-at > price).
    showCompareAtPrice: new UntypedFormControl(values?.['showCompareAtPrice'] ?? true),
    // BUTTON block
    // Button design overrides — null means "inherit the global Button Design" (see the
    // "Reset to global color" affordance). The editor resolves widget-value ?? global for display.
    buttonBackgroundColor: new UntypedFormControl(values?.['buttonBackgroundColor'] ?? null),
    buttonTextColor: new UntypedFormControl(values?.['buttonTextColor'] ?? null),
    buttonHoverColor: new UntypedFormControl(values?.['buttonHoverColor'] ?? null),
    buttonIconColor: new UntypedFormControl(values?.['buttonIconColor'] ?? null),
    buttonCornerRadius: new UntypedFormControl(values?.['buttonCornerRadius'] ?? null),
    textAddToOrder: new UntypedFormControl(values?.['textAddToOrder'] ?? 'Add to order', Validators.required),
    variantButtonText: new UntypedFormControl(values?.['variantButtonText'] ?? 'Select an option'),
    additionalDisplayTexts: createAdditionalDisplayTexts(
      values?.['additionalDisplayTexts'] as AdditionalDisplayTextValue[] | undefined,
    ),
  });
}

export function createProductSelectionFormGroup(
  values?: ProductSelectionValue,
  rulesDynamicFormService?: RulesDynamicFormService,
): UntypedFormGroup {
  const ruleGroupValues = values?.ruleGroups?.length ? values.ruleGroups : [{ ...DEFAULT_RULE_GROUP, id: 0 }];
  const ruleGroups = new UntypedFormArray(
    ruleGroupValues.map((value, index) => createRuleGroupFormGroup(value.id ?? index, value, rulesDynamicFormService)),
  );

  return new UntypedFormGroup({
    ruleGroups,
    style: createAppearanceStyleGroup(values?.style),
    settings: createAppearanceSettingsGroup(values?.settings),
  });
}
